//frontend/src/components/Chat/ChatRooms.jsx -- левая колонка со списком чатов/комнат

import React, { useMemo } from "react";

import "./ChatRooms.css";


function getRoomName(room) {
  if (!room) {
    return "Без названия";
  }

  return (
    room.name ||
    room.getCanonicalAlias?.() ||
    room.getDefaultRoomName?.(
      room.myUserId
    ) ||
    room.roomId ||
    "Без названия"
  );
}


function getRoomInitial(roomName) {
  if (!roomName) {
    return "?";
  }

  return roomName
    .trim()
    .charAt(0)
    .toUpperCase();
}


function getLastMessageEvent(room) {
  if (!room) {
    return null;
  }

  const event = room.getLastLiveEvent?.();

  if (!event) {
    return null;
  }

  if (
    event.getType?.() !==
    "m.room.message"
  ) {
    return null;
  }

  return event;
}


function getRoomPreview(room) {
  const event = getLastMessageEvent(room);

  if (!event) {
    return "Нет сообщений";
  }

  const content =
    event.getContent?.() || {};

  const messageType =
    content.msgtype;

  switch (messageType) {
    case "m.text":
      return (
        content.body ||
        "Сообщение"
      );

    case "m.image":
      return "🖼️ Изображение";

    case "m.video":
      return "🎬 Видео";

    case "m.audio":
      return "🎙️ Голосовое сообщение";

    case "m.file":
      return `📎 ${
        content.filename ||
        content.body ||
        "Файл"
      }`;

    default:
      return (
        content.body ||
        "Сообщение"
      );
  }
}


function getRoomTime(room) {
  const event =
    getLastMessageEvent(room);

  if (!event) {
    return "";
  }

  const timestamp =
    event.getTs?.();

  if (!timestamp) {
    return "";
  }

  return new Date(
    timestamp
  ).toLocaleTimeString(
    "ru-RU",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );
}


function getUnreadCount(room) {
  if (!room) {
    return 0;
  }

  return (
    room.getRoomUnreadNotificationCount?.() ||
    0
  );
}


function getMemberCount(room) {
  if (!room) {
    return 0;
  }

  return (
    room.getJoinedMemberCount?.() ||
    0
  );
}


function getRoomTimestamp(room) {
  if (!room) {
    return 0;
  }

  return (
    room.getLastActiveTimestamp?.() ||
    room.getBumpStamp?.() ||
    0
  );
}


function sortRooms(rooms) {
  return [...rooms].sort(
    (a, b) =>
      getRoomTimestamp(b) -
      getRoomTimestamp(a)
  );
}


export default function ChatRooms({
  rooms = [],
  selectedRoomId,
  onSelectRoom,
}) {
  const sortedRooms = useMemo(
    () => sortRooms(rooms),
    [rooms]
  );

  return (
    <aside className="chat-rooms">

      <header className="chat-rooms__header">

        <div className="chat-rooms__header-info">

          <h2>
            Сообщения
          </h2>

          <span>
            {sortedRooms.length}{" "}
            {sortedRooms.length === 1
              ? "комната"
              : "комнат"}
          </span>

        </div>

      </header>


      <div className="chat-rooms__list">

        {sortedRooms.length === 0 ? (

          <div className="chat-rooms__empty">

            <div className="chat-rooms__empty-icon">
              💬
            </div>

            <strong>
              Нет доступных комнат
            </strong>

            <span>
              Здесь появятся ваши чаты
            </span>

          </div>

        ) : (

          sortedRooms.map((room) => {
            const roomId =
              room.roomId;

            const roomName =
              getRoomName(room);

            const roomInitial =
              getRoomInitial(
                roomName
              );

            const preview =
              getRoomPreview(room);

            const time =
              getRoomTime(room);

            const unread =
              getUnreadCount(room);

            const memberCount =
              getMemberCount(room);

            const isActive =
              selectedRoomId === roomId;

            return (
              <button
                key={roomId}
                type="button"
                className={[
                  "chat-room-item",
                  isActive
                    ? "is-active"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() =>
                  onSelectRoom?.(
                    roomId
                  )
                }
              >

                <div className="chat-room-item__avatar">
                  {roomInitial}
                </div>


                <div className="chat-room-item__body">

                  <div className="chat-room-item__top">

                    <strong
                      title={roomName}
                    >
                      {roomName}
                    </strong>

                    {time && (
                      <span>
                        {time}
                      </span>
                    )}

                  </div>


                  <div className="chat-room-item__bottom">

                    <span className="chat-room-item__preview">
                      {preview}
                    </span>


                    <div className="chat-room-item__meta">

                      {memberCount > 2 && (
                        <span
                          className="chat-room-item__members"
                          title={`${memberCount} участников`}
                        >
                          {memberCount}
                        </span>
                      )}


                      {unread > 0 && (
                        <span className="chat-room-item__unread">
                          {unread > 99
                            ? "99+"
                            : unread}
                        </span>
                      )}

                    </div>

                  </div>

                </div>

              </button>
            );
          })

        )}

      </div>

    </aside>
  );
}